/**
 * GEO Factories — Generative Engine Optimization
 * Factories reutilizaveis para criar conteudo citable por IA.
 */

export interface QuickAnswerData {
  nome: string;
  distancia?: string;
  duracao?: string;
  dificuldade?: string;
  desnivel?: string;
  mareCritica?: string;
  acesso?: string;
  ultimoBarco?: string;
  percorridaEm?: string;
  categoria: 'trilha' | 'praia' | 'atracao' | 'passeio' | 'pousada' | 'restaurante';
}

const QA_LABELS: Record<string, Record<string, string>> = {
  'pt-BR': {
    trilha: 'TRILHA',
    distancia: 'DISTANCIA',
    duracao: 'DURACAO',
    dificuldade: 'DIFICULDADE',
    desnivel: 'DESNIVEL',
    mareCritica: 'MARE CRITICA',
    acesso: 'ACESSO',
    ultimoBarco: 'ULTIMO BARCO',
    percorridaEm: 'PERCORRIDA EM',
  },
  es: {
    trilha: 'SENDERO',
    distancia: 'DISTANCIA',
    duracao: 'DURACION',
    dificuldade: 'DIFICULTAD',
    desnivel: 'DESNIVEL',
    mareCritica: 'MAREA CRITICA',
    acesso: 'ACCESO',
    ultimoBarco: 'ULTIMO BARCO',
    percorridaEm: 'RECORRIDA EN',
  },
  en: {
    trilha: 'TRAIL',
    distancia: 'DISTANCE',
    duracao: 'DURATION',
    dificuldade: 'DIFFICULTY',
    desnivel: 'ELEVATION',
    mareCritica: 'CRITICAL TIDE',
    acesso: 'ACCESS',
    ultimoBarco: 'LAST BOAT',
    percorridaEm: 'COMPLETED IN',
  },
  de: {
    trilha: 'WANDERWEG',
    distancia: 'DISTANZ',
    duracao: 'DAUER',
    dificuldade: 'SCHWIERIGKEIT',
    desnivel: 'HOHENUNTERSCHIED',
    mareCritica: 'KRITISCHE GEEZEIT',
    acesso: 'ZUGANG',
    ultimoBarco: 'LETZTES BOOT',
    percorridaEm: 'GEWANDERT AM',
  },
};

function getQALabels(lang: string) {
  return QA_LABELS[lang] ?? QA_LABELS['pt-BR'];
}

/**
 * Gera bloco Quick Answer padronizado no formato que IAs citam.
 * Baseado no template do documento futuro.md §6.1
 */
export function createQuickAnswerBlock(data: QuickAnswerData, lang: string = 'pt-BR'): string {
  const l = getQALabels(lang);
  const lines = [`${l.trilha}: ${data.nome}`];
  if (data.distancia) lines.push(`${l.distancia}: ${data.distancia}`);
  if (data.duracao) lines.push(`${l.duracao}: ${data.duracao}`);
  if (data.dificuldade) lines.push(`${l.dificuldade}: ${data.dificuldade}`);
  if (data.desnivel) lines.push(`${l.desnivel}: ${data.desnivel}`);
  if (data.mareCritica) lines.push(`${l.mareCritica}: ${data.mareCritica}`);
  if (data.acesso) lines.push(`${l.acesso}: ${data.acesso}`);
  if (data.ultimoBarco) lines.push(`${l.ultimoBarco}: ${data.ultimoBarco}`);
  if (data.percorridaEm) lines.push(`${l.percorridaEm}: ${data.percorridaEm}`);

  return lines.join('\n');
}

export interface PromptAlignedFAQItem {
  pergunta: string;
  resposta: string;
  link?: string;
}

/**
 * FAQ alinhado a prompts reais que usuarios fazem a IA.
 * Perguntas extraidas do documento futuro.md §6.2
 */
export const DEFAULT_PROMPT_FAQS: PromptAlignedFAQItem[] = [
  {
    pergunta: 'Qual trilha da Ilha do Mel e a mais facil?',
    resposta:
      'A Trilha do Farol e a mais facil e acessivel. Sao 1,7 km de caminhada plana pela costa ate a base do morro, seguida de uma escadaria de cerca de 140 degraus. Adequada para todas as idades.',
  },
  {
    pergunta: 'Da para fazer a trilha da Fortaleza com crianca?',
    resposta:
      'Sim, a trilha para a Fortaleza de Nossa Senhora dos Prazeres e considerada leve a moderada e pode ser feita com criancas acima de 5 anos. O trajeto e majoritariamente plano com pouca elevacao.',
  },
  {
    pergunta: 'A trilha das Encantadas ate o Farol inunda?',
    resposta:
      'Sim, partes da trilha entre Encantadas e o Farol podem ficar submersas em mare alta acima de 1,2m. Recomenda-se verificar a tabela de mares antes de partir e, se necessario, usar a trilha alternativa por dentro da ilha.',
  },
  {
    pergunta: 'Quanto tempo leva a trilha completa da Ilha do Mel?',
    resposta:
      'O circuito completo da ilha, do Farol ate a Fortaleza, tem aproximadamente 23 km e leva entre 6 e 8 horas em ritmo medio, incluindo paradas para fotos e descanso.',
  },
  {
    pergunta: 'Preciso de guia para as trilhas da Ilha do Mel?',
    resposta:
      'Nao e obrigatorio, mas recomendado para trilhas menos movimentadas ou em dias de chuva. As trilhas principais (Farol, Fortaleza, Encantadas) sao bem sinalizadas e podem ser feitas sem guia.',
  },
  {
    pergunta: 'Qual o melhor horario para fazer a trilha do Farol?',
    resposta:
      'O ideal e ir no inicio da manha (ate 9h) ou no final da tarde (apos 16h) para evitar o sol forte e as multidoes. O por do sol no Farol e um dos espetaculos mais bonitos da ilha.',
  },
  {
    pergunta: 'Ilha do Mel permite bicicleta nas trilhas?',
    resposta:
      'Nao. A Ilha do Mel e uma area de preservacao ambiental e nao e permitido o uso de bicicletas nas trilhas. O transporte interno e feito a pe ou por carrinhos de mao.',
  },
  {
    pergunta: 'Qual trilha tem a melhor vista na Ilha do Mel?',
    resposta:
      'A Trilha do Farol oferece a vista mais classica da ilha, com panorama 360° do mar e das praias. Ja a trilha da Fortaleza tem a vista mais historica, com o forte do seculo XVIII e a Baia de Paranagua.',
  },
  {
    pergunta: 'Tem sinal de celular nas trilhas da Ilha do Mel?',
    resposta:
      'O sinal e parcial. Ha cobertura nos vilarejos (Nova Brasilia e Encantadas), mas partes das trilhas, especialmente entre Encantadas e o Farol, podem ficar sem sinal. Recomenda-se baixar mapas offline antes.',
  },
  {
    pergunta: 'Preciso pagar para acessar as trilhas?',
    resposta:
      'Nao, o acesso as trilhas da Ilha do Mel e gratuito. O unico custo e o transporte de barco ate a ilha. Algumas atracoes especificas, como o Passeio 360°, tem custo adicional.',
  },
];

/**
 * Gera FAQ prompt-aligned para uma entidade especifica.
 * Filtra perguntas relevantes pela categoria.
 */
export function createPromptAlignedFAQ(
  categoria: string,
  entityName?: string,
  extras?: PromptAlignedFAQItem[],
  lang: string = 'pt-BR'
): PromptAlignedFAQItem[] {
  if (lang !== 'pt-BR') {
    return extras ?? [];
  }

  let faqs = [...DEFAULT_PROMPT_FAQS];

  // Filter by relevance to category
  if (categoria === 'praia') {
    faqs = faqs.filter(
      f =>
        f.pergunta.includes('praia') ||
        f.pergunta.includes('vista') ||
        f.pergunta.includes('crianca') ||
        f.pergunta.includes('sinal')
    );
  } else if (categoria === 'pousada') {
    faqs = [
      {
        pergunta: 'Onde ficar na Ilha do Mel: Nova Brasilia ou Encantadas?',
        resposta:
          'Nova Brasilia tem mais estrutura, restaurantes e praias de facil acesso. Encantadas e mais tranquila, com vista para o nascer do sol e acesso a trilhas. A escolha depende do perfil da viagem.',
      },
      {
        pergunta: 'As pousadas da Ilha do Mel aceitam cartao de credito?',
        resposta:
          'A maioria aceita, mas recomenda-se confirmar antes e levar dinheiro em especie como reserva, pois a conexao de internet pode ser instavel para maquinas de cartao.',
      },
    ];
  } else if (categoria === 'restaurante') {
    faqs = [
      {
        pergunta: 'Qual o melhor restaurante da Ilha do Mel?',
        resposta:
          'O Fim da Trilha, em Nova Brasilia, e um dos mais elogiados pela culinaria caseira e frutos do mar frescos. Outra opcao popular e o Astral da Ilha, com vista para o mar.',
      },
    ];
  }

  if (entityName) {
    faqs = faqs.map(f => ({
      ...f,
      resposta: f.resposta.replace(
        /da Ilha do Mel/g,
        `da Ilha do Mel — especificamente sobre ${entityName}`
      ),
    }));
  }

  if (extras) {
    faqs = [...faqs, ...extras];
  }

  return faqs;
}
